/**
 * The command half of the command → event contract (architecture §5.3).
 *
 *   apply(state: WorldState, cmd: Command, rng: SealedRng): { state, events }
 *
 * A command is an intent; it produces events or a `RulesError` and nothing else.
 * The model never issues combat actions — they come from the player's action bar,
 * the deterministic parser, or the enemy utility AI. There is deliberately no
 * `roll_dice`, `apply_damage`, `set_hp`, `set_price`, `reroll` or `fudge` command.
 */
import { z } from 'zod';
import { Advantage, Cell, CheckKey, DifficultyTier, RollKind, RollMode, ConditionId, SpellAffliction } from './primitives.js';
import { StartCombatInput } from './gm-tools.js';
import { CombatOutcome } from './scene-spec.js';
/* ------------------------------------------------------------- envelope */
export const CommandOrigin = z.enum(['player', 'gm_tool', 'tick', 'director', 'system']);
const commandEnvelope = z.object({
    id: z.string().min(1),
    actorId: z.string().min(1),
    origin: CommandOrigin,
    /** tool_call id, storylet id, tick id. */
    causeRef: z.string().optional(),
});
function defineCommand(kind, payload) {
    return commandEnvelope.extend({ kind: z.literal(kind), payload });
}
/* --------------------------------------------------- combat action payloads */
export const MovePayload = z.object({ path: z.array(Cell).min(1) });
/**
 * A destination or a target, the path being the engine's to find (§6.4): the
 * board click of the founder's first playtest, and "I move toward him".
 */
export const MoveToPayload = z
    .object({ to: Cell.optional(), toward: z.string().optional() })
    .refine((p) => p.to !== undefined || p.toward !== undefined, { message: 'to or toward' });
export const AttackPayload = z.object({ attackId: z.string(), targetId: z.string() });
export const CastPayload = z.object({
    spellId: z.string(),
    slotLevel: z.number().int().min(0).max(9).optional(),
    targets: z.array(z.string()).default([]),
    origin: Cell.optional(),
    /** ADR-094: cast as a ritual — ten minutes longer, no slot; only a spell with the ritual tag, never in a fight. */
    ritual: z.boolean().optional(),
    /** ADR-096 (Suggestion): the course of activity, in the caster's words — the SRD's twenty-five words at most. */
    course: z.string().max(400).optional(),
});
/** ADR-096: the narrator says a spell's condition has run its course — a Suggestion carried out. */
export const EndConditionPayload = z.object({
    target: z.string(),
    condition: z.union([ConditionId, SpellAffliction]),
    source: z.string().optional(),
});
export const NoPayload = z.object({});
export const HelpPayload = z.object({ targetId: z.string() });
export const UseObjectPayload = z.object({ propId: z.string(), interaction: z.string() });
export const ReadyPayload = z.object({ trigger: z.string().max(160), action: z.string() });
export const GrapplePayload = z.object({ targetId: z.string() });
export const ShovePayload = z.object({
    targetId: z.string(),
    mode: z.enum(['prone', 'push']),
});
export const UseFeaturePayload = z.object({
    featureId: z.string(),
    targetId: z.string().optional(),
    choices: z.record(z.string(), z.unknown()).default({}),
});
export const UseItemPayload = z.object({
    itemId: z.string(),
    targetId: z.string().optional(),
});
export const ReactionPayload = z.object({
    reactionId: z.string(),
    triggerSeq: z.number().int().nonnegative(),
});
/** Declared before the attack that would kill, per SRD "knocking a creature out". */
export const KnockOutPayload = z.object({ targetId: z.string() });
/* ------------------------------------------------- check and save payloads */
export const CheckPayload = z.object({
    actorId: z.string(),
    kind: z.enum(['skill', 'ability', 'save']),
    key: CheckKey,
    /** Exactly one of `dc` (static) or `contest` (opposed) is set. */
    dc: z.number().int().optional(),
    tier: DifficultyTier.optional(),
    contest: z
        .object({ againstId: z.string(), key: CheckKey })
        .optional(),
    advantage: Advantage.default('none'),
    stakes: z.string().max(160).default(''),
    consequenceOnFail: z.string().default('none'),
    /** ADR-091: what a save is against, so a trait that grants advantage against it applies. */
    against: ConditionId.optional(),
    /**
     * A flat modifier the session layer adds to the sheet's — ADR-077's argument
     * assessment is the one user. Bounded here so nothing upstream can hand the
     * engine a +20; the engine adds it, shows it in the breakdown, and never
     * decides it.
     */
    bonus: z.number().int().min(-5).max(5).default(0),
});
export const DeathSavePayload = z.object({ actorId: z.string() });
export const ConcentrationCheckPayload = z.object({
    actorId: z.string(),
    dc: z.number().int().min(10),
    damageSeq: z.number().int().nonnegative(),
});
/* --------------------------------------------------- roll control payloads */
export const ResolveRollPayload = z.object({
    rollId: z.string(),
    /** sha256 of the client's serialized throw gesture (§5.4). */
    entropyHash: z.string().regex(/^[0-9a-f]{64}$/),
    clientThrowId: z.string(),
});
export const SkipRollPayload = z.object({
    rollId: z.string(),
    reason: z.enum(['timeout', 'player_asked']),
});
export const SetRollModePayload = z.object({ kind: RollKind, mode: RollMode });
/* ---------------------------------------------------- scene/world payloads */
export const EndCombatPayload = z.object({ outcome: CombatOutcome });
/**
 * Roll initiative and open the turn order (§6.8, ADR-073's sibling in the engine).
 *
 * Separate from `start_combat`, which is the GM tool that builds the board: this
 * one carries no fiction and no scene, only who is in the fight and what their
 * initiative bonus is, because that is all the rules engine can answer for. The
 * pod issues it once the board exists.
 */
export const StartEncounterPayload = z
    .object({
    participants: z
        .array(z.object({
        actorId: z.string(),
        team: z.enum(['party', 'hostile', 'bystander']),
        initiativeBonus: z.number().int().min(-5).max(20).default(0),
    }))
        .min(1)
        .max(24),
})
    .strict();
/**
 * Add tags to the session's lines (ADR-063 §5). Additive and never subtractive:
 * a line, once drawn, is not something the engine offers to rub out.
 */
/** ADR-087: the game master's prep for the sitting, recorded before the first turn. */
/** ADR-088: a journey to a known place along known roads; the engine rolls its legs. */
export const JourneyPayload = z.object({ destinationRef: z.string() });
export const PlanSessionPayload = z.object({
    sheetBy: z.enum(['engine', 'persona']).default('engine'),
    lengthMinutes: z.number().int().min(10),
    kickstarters: z.array(z.string()).max(6).default([]),
    peaks: z.array(z.string()).max(6).default([]),
    landing: z.string().nullable().default(null),
    sheet: z.string().max(1200).default(''),
});
export const SafetyLinePayload = z
    .object({ tags: z.array(z.string().min(1).max(40)).min(1).max(20) })
    .strict();
/** End the acting actor's turn and pass to the next in the order (§6.8). */
export const EndEncounterTurnPayload = z.object({ actorId: z.string() }).strict();
export const SetScenePayload = z.object({
    locationRef: z.string(),
    moodTag: z.string().max(40).default(''),
});
export const TravelPayload = z.object({
    destinationRef: z.string(),
    pace: z.enum(['slow', 'normal', 'fast']),
});
export const AdvanceTimePayload = z.object({
    unit: z.enum(['scene', 'period', 'short_rest', 'long_rest', 'days']),
    n: z.number().int().min(1).max(7).default(1),
});
export const RestPayload = z.object({ kind: z.enum(['short', 'long']) });
/** ADR-088 §5: a night in camp — watches with a hazard roll each, a long rest if the night is quiet. */
export const CampPayload = z.object({
    /** ADR-088 §5: the watch order — `player` or present NPC refs, cycled over the watches. Empty means the player alone. */
    watches: z.array(z.string()).max(6).default([]),
});
/**
 * Coin changes hands (2026-09-07). The player's own words ("I put two silver on the
 * counter"), a table that closed on a gold slot, or the narrator's `transfer_coins`
 * for a price the fiction settled. `pay` is refused when the purse is short.
 */
export const TransferCoinsPayload = z.object({
    direction: z.enum(['pay', 'receive']),
    /** Who the coin goes to or comes from; empty when nobody in particular. */
    counterpart: z.string().default(''),
    amountCp: z.number().int().min(1).max(1_000_000),
    reason: z.string().max(120).default(''),
});
export const TradePayload = z.object({
    npc: z.string(),
    itemId: z.string(),
    qty: z.number().int().min(1).max(99),
    side: z.enum(['buy', 'sell']),
});
/**
 * ADR-129 (#61): the player takes what a defeated enemy left. `query` is their own words for it —
 * "the mace", "as armas", "everything" — resolved by the engine against the remains at the
 * player's location and nothing else. No tool offers this; it is the player's request.
 */
export const TakeItemPayload = z.object({
    query: z.string().min(1),
});
export const RelationshipDeltaPayload = z.object({
    npc: z.string(),
    trust: z.number().int(),
    respect: z.number().int(),
    affinity: z.number().int(),
    fear: z.number().int(),
    memoryNote: z.string().max(120).default(''),
});
export const AssertFactPayload = z.object({
    entityRef: z.string(),
    fact: z.string().max(200),
    kind: z.enum(['appearance', 'backstory', 'belief', 'world']),
});
/** ADR-113: places a narration named, sent by the pod after the narration is spoken. */
export const LearnPlacesPayload = z.object({
    locationRefs: z.array(z.string().min(1)).min(1).max(12),
});
export const QuestAdvancePayload = z.object({
    questId: z.string(),
    toStage: z.string(),
    evidence: z.string().max(160),
});
export const PromisePayload = z.object({
    kind: z.enum(['threat', 'reward', 'mystery', 'reunion']),
    text: z.string().max(160),
    payoffBySession: z.number().int().min(1).max(3),
});
export const EmitCuePayload = z.object({
    kind: z.enum(['sfx', 'music_stem', 'weather', 'lighting']),
    id: z.string(),
    intensity: z.number().int().min(1).max(3).default(1),
});
export const OfferChoicesPayload = z.object({
    options: z.array(z.string().max(60)).min(2).max(4),
});
export const LevelUpPayload = z.object({
    actorId: z.string(),
    toLevel: z.number().int().min(2).max(20),
    milestoneRef: z.string(),
});
export const SuccessionPayload = z.object({ successorRef: z.string() });
/* -------------------------------------------------- negotiation payloads */
/** The negotiation peak machine (§6.10); ships in M1a, before the grid. */
export const NegotiationSlot = z.enum(['gold', 'favour', 'information', 'time', 'risk']);
export const OfferPayload = z.object({
    slot: NegotiationSlot,
    value: z.number().int(),
});
export const PressPayload = z.object({ leverId: z.string() });
export const ConcedePayload = z.object({ slot: NegotiationSlot });
export const ReadRoomPayload = z.object({ targetRef: z.string() });
export const WalkAwayPayload = z.object({});
export const NegotiationStance = z.enum(['hostile', 'guarded', 'open', 'invested']);
export const LeverKind = z.enum(['fact', 'debt', 'threat', 'good']);
/** A lever as the pack or the fiction supplies it: usable once, deterministic in effect. */
export const NegotiationLever = z.object({
    id: z.string(),
    kind: LeverKind,
    label: z.string().max(120),
    /** For `good`: the slot it moves and by how much. */
    slot: NegotiationSlot.optional(),
    value: z.number().int().optional(),
    /** NPC levers start hidden; `read_room` or the fiction reveals them. */
    hidden: z.boolean().default(false),
});
/**
 * Opens the machine (§6.10). Issued by the actor when the fiction produces a
 * negotiation — a storylet with `peakKind: negotiation` engaged, a haggle at a
 * stall — never by a GM tool: the fourteen tools are §3.3's surface and a
 * negotiation is a scene the engine runs, like a fight.
 */
export const OpenNegotiationPayload = z.object({
    with: z.array(z.string()).min(1),
    stake: z.string().max(160),
    /**
     * Which way the slot moves. `player_receives`: the player asks for more than
     * the NPC offers (a fee, a favour, information). `player_pays`: the player
     * offers less than the NPC asks (a price). The gap and every concession read
     * from this; without it a haggle over a room's price ran backwards.
     */
    direction: z.enum(['player_receives', 'player_pays']).default('player_receives'),
    playerAsk: z.object({ slot: NegotiationSlot, value: z.number().int() }),
    npcOffer: z.object({ slot: NegotiationSlot, value: z.number().int() }),
    stance: NegotiationStance,
    patience: z.number().int().min(3).max(6),
    playerLevers: z.array(NegotiationLever).default([]),
    npcLevers: z.array(NegotiationLever).default([]),
    /** Display names for counterparties the world has no card for (a stall-hand). */
    names: z.record(z.string(), z.string()).default({}),
});
/**
 * ADR-077: a spoken skill check inside a negotiation. `argumentModifier` is the
 * bounded assessment of the player's words, made before the roll and clamped by
 * the engine; `rationale` is the one line the dice log shows for it. The engine
 * sets the DC from stance and stake and rolls d20 + skill + this.
 */
export const ArguePayload = z.object({
    actorId: z.string(),
    skill: z.enum(['persuasion', 'intimidation', 'deception', 'performance']),
    leverId: z.string().optional(),
    argumentModifier: z.number().int().min(-2).max(3).default(0),
    rationale: z.string().max(160).default(''),
    /** The words, for the log; never read by the engine. */
    utterance: z.string().max(400).default(''),
});
/* ------------------------------------------------- lifecycle/tick payloads */
export const StartSessionPayload = z.object({
    campaignId: z.string(),
    /** sha256(sessionSeed), published in the lobby and the Ledger (§5.4). */
    seedCommitment: z.string().regex(/^[0-9a-f]{64}$/),
});
export const EndSessionPayload = z.object({
    /* ADR-130: `death` — the character died; a forced end, narrated by the turn that killed. */
    reason: z.enum(['player_asked', 'gm_tool', 'hard_stop', 'grace_expired', 'server_abort', 'death']),
    cliffhangerHookRef: z.string().default(''),
});
/* ------------------------------------------------------------ the union */
/**
 * The X-card (ADR-063 §5). `source` is how it was played and `turnId` is which
 * turn it interrupted; there is deliberately no field describing what the player
 * was reacting to. The audit is that a line was drawn, not a record of the thing
 * it was drawn around.
 */
export const SafetyCardPayload = z
    .object({
    turnId: z.string().default(''),
    source: z.enum(['voice', 'button', 'text']).default('button'),
})
    .strict();
export const Command = z.discriminatedUnion('kind', [
    // Combat actions
    defineCommand('move', MovePayload),
    defineCommand('move_to', MoveToPayload),
    defineCommand('attack', AttackPayload),
    defineCommand('cast', CastPayload),
    defineCommand('dash', NoPayload),
    defineCommand('disengage', NoPayload),
    defineCommand('dodge', NoPayload),
    defineCommand('help', HelpPayload),
    defineCommand('hide', NoPayload),
    defineCommand('search', NoPayload),
    defineCommand('use_object', UseObjectPayload),
    defineCommand('ready', ReadyPayload),
    defineCommand('grapple', GrapplePayload),
    defineCommand('shove', ShovePayload),
    defineCommand('escape_grapple', NoPayload),
    defineCommand('stand_up', NoPayload),
    defineCommand('drop_prone', NoPayload),
    defineCommand('use_feature', UseFeaturePayload),
    defineCommand('use_item', UseItemPayload),
    defineCommand('reaction', ReactionPayload),
    defineCommand('knock_out', KnockOutPayload),
    defineCommand('end_turn', NoPayload),
    // Checks and saves
    defineCommand('check', CheckPayload),
    defineCommand('death_save', DeathSavePayload),
    defineCommand('concentration_check', ConcentrationCheckPayload),
    // Roll control
    defineCommand('resolve_roll', ResolveRollPayload),
    defineCommand('skip_roll', SkipRollPayload),
    defineCommand('set_roll_mode', SetRollModePayload),
    // Scene and world
    defineCommand('start_combat', StartCombatInput),
    defineCommand('end_combat', EndCombatPayload),
    defineCommand('start_encounter', StartEncounterPayload),
    defineCommand('end_encounter_turn', EndEncounterTurnPayload),
    defineCommand('set_scene', SetScenePayload),
    defineCommand('travel', TravelPayload),
    defineCommand('advance_time', AdvanceTimePayload),
    defineCommand('rest', RestPayload),
    defineCommand('camp', CampPayload),
    defineCommand('trade', TradePayload),
    defineCommand('take_item', TakeItemPayload),
    defineCommand('transfer_coins', TransferCoinsPayload),
    defineCommand('relationship_delta', RelationshipDeltaPayload),
    defineCommand('assert_fact', AssertFactPayload),
    defineCommand('learn_places', LearnPlacesPayload),
    defineCommand('quest_advance', QuestAdvancePayload),
    defineCommand('promise', PromisePayload),
    defineCommand('emit_cue', EmitCuePayload),
    defineCommand('offer_choices', OfferChoicesPayload),
    defineCommand('level_up', LevelUpPayload),
    defineCommand('end_condition', EndConditionPayload),
    defineCommand('succession', SuccessionPayload),
    // Negotiation peak machine
    defineCommand('offer', OfferPayload),
    defineCommand('press', PressPayload),
    defineCommand('concede', ConcedePayload),
    defineCommand('read_room', ReadRoomPayload),
    defineCommand('walk_away', WalkAwayPayload),
    defineCommand('open_negotiation', OpenNegotiationPayload),
    defineCommand('argue', ArguePayload),
    // Ticks and lifecycle
    defineCommand('period_tick', NoPayload),
    /** A turn in which nothing charged the clock costs a beat (2026-09-08). */
    defineCommand('beat', NoPayload),
    defineCommand('day_tick', NoPayload),
    defineCommand('start_session', StartSessionPayload),
    defineCommand('end_session', EndSessionPayload),
    defineCommand('pause', NoPayload),
    defineCommand('resume', NoPayload),
    // Safety (ADR-063 §5). A command like any other, so it is ordered on the single
    // writer's queue, logged in the same append-only log, and replays.
    defineCommand('safety_card', SafetyCardPayload),
    defineCommand('safety_line', SafetyLinePayload),
    defineCommand('plan_session', PlanSessionPayload),
    defineCommand('journey', JourneyPayload),
]);
export const COMMAND_KINDS = Command.options.map((o) => o.shape.kind.value);
export const CommandKind = z.enum(COMMAND_KINDS);
/**
 * Commands accepted while the session is in `AwaitingRoll` (§5.3, §8.2).
 * Everything else is rejected with `RulesError.code = 'AwaitingRoll'`.
 */
export const AWAITING_ROLL_ALLOWED_COMMANDS = Object.freeze([
    'resolve_roll',
    'skip_roll',
    'set_roll_mode',
    'pause',
    /* The two safety tools (ADR-063 §5). A pending roll is one of the states a
     * player is most likely to want them in — the die is on the table and the
     * outcome is the thing being reached for — and a safety tool that can be locked
     * out by a state is not one. Neither throws a die nor touches anything the
     * pending roll depends on. */
    'safety_card',
    'safety_line',
]);
//# sourceMappingURL=commands.js.map